import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../api/header";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Spinner } from "../../components/ui/spinner";
import { Switch } from "../../components/ui/switch";
import DefaultEditor from "react-simple-wysiwyg";
import { ReactSelect } from "../../components/ui/react-select";

const ProductModal = ({
  open,
  onOpenChange,
  onSuccess,
  defaultTrackingType = "none",
  defaultManageInventory = false,
}) => {
  const queryClient = useQueryClient();

  const [categorySearchInput, setCategorySearchInput] = useState("");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [modelSearchInput, setModelSearchInput] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [unitSearchInput, setUnitSearchInput] = useState("");
  const [unitSearchTerm, setUnitSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setCategorySearchTerm(categorySearchInput), 300);
    return () => clearTimeout(timer);
  }, [categorySearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => setBrandSearchTerm(brandSearchInput), 300);
    return () => clearTimeout(timer);
  }, [brandSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => setModelSearchTerm(modelSearchInput), 300);
    return () => clearTimeout(timer);
  }, [modelSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => setUnitSearchTerm(unitSearchInput), 300);
    return () => clearTimeout(timer);
  }, [unitSearchInput]);

  const [newProductData, setNewProductData] = useState({
    name: "",
    sku: "",
    short_description: "",
    unit_id: null,
    brand_id: null,
    model_id: null,
    categories: [],
    cost: 0,
    sell_price: 0,
    sell_price_min: 0,
    sell_price_max: 0,
    alert_quantity: 0,
    warranty: 0,
    type: "simple",
    tax_method: "exclusive",
    manage_inventory: defaultManageInventory,
    tracking_type: defaultTrackingType,
    display_on_pos: true,
    status: "active",
    warranty_timeframe: "No Warranty",
  });

  const { data: rawUnits = [] } = useQuery({
    queryKey: ["units", unitSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.UNITS, {
        params: { perPage: 100, search: unitSearchTerm || undefined },
      });
      return res.data?.data || res.data || [];
    },
    enabled: open,
  });

  const units = useMemo(() => {
    if (Array.isArray(rawUnits)) return rawUnits;
    if (Array.isArray(rawUnits?.data)) return rawUnits.data;
    return [];
  }, [rawUnits]);

  const { data: rawCategories = [] } = useQuery({
    queryKey: ["categories", categorySearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.CATEGORIES, {
        params: { perPage: 100, search: categorySearchTerm || undefined },
      });
      return res.data?.data || res.data || [];
    },
    enabled: open,
  });

  const categories = useMemo(() => {
    if (Array.isArray(rawCategories)) return rawCategories;
    if (Array.isArray(rawCategories?.data)) return rawCategories.data;
    return [];
  }, [rawCategories]);

  const { data: rawBrands = [] } = useQuery({
    queryKey: ["brands", brandSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.BRANDS, {
        params: { perPage: 100, search: brandSearchTerm || undefined },
      });
      return res.data?.data || res.data || [];
    },
    enabled: open,
  });

  const brands = useMemo(() => {
    if (Array.isArray(rawBrands)) return rawBrands;
    if (Array.isArray(rawBrands?.data)) return rawBrands.data;
    return [];
  }, [rawBrands]);

  const { data: rawModels = [] } = useQuery({
    queryKey: ["models", newProductData.brand_id, modelSearchTerm],
    queryFn: async () => {
      if (!newProductData.brand_id) return [];
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.BRAND_MODELS(newProductData.brand_id),
        { params: { perPage: 100, search: modelSearchTerm || undefined } }
      );
      const list = res.data?.data || res.data || [];
      return Array.isArray(list) ? list.filter((model) => model.status === "active") : [];
    },
    enabled: !!newProductData.brand_id && open,
  });

  const models = useMemo(() => {
    if (Array.isArray(rawModels)) return rawModels;
    if (Array.isArray(rawModels?.data)) return rawModels.data;
    return [];
  }, [rawModels]);

  const unitOptions = useMemo(
    () => units.map((u) => ({ value: Number(u.id), label: u.name })),
    [units]
  );
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: Number(c.id), label: c.name })),
    [categories]
  );
  const brandOptions = useMemo(
    () => brands.map((b) => ({ value: Number(b.id), label: b.name })),
    [brands]
  );
  const modelOptions = useMemo(
    () => models.map((m) => ({ value: Number(m.id), label: m.name })),
    [models]
  );

  const createProductMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await apiClient.post(API_ENDPOINTS.INVENTORY.PRODUCTS, payload);
      const data = res.data?.data || res.data;
      return { ...payload, ...data };
    },
    onSuccess: async (newProduct) => {
      toast.success("Product created successfully");

      // Invalidate queries to ensure fresh data eventually
      queryClient.invalidateQueries({ queryKey: ["posProducts"] });
      queryClient.invalidateQueries({ queryKey: ["all-pos-products"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["pos-products"] });

      // Optimistically insert the new product into the pos-products (infinite query) cache
      queryClient.setQueriesData({ queryKey: ["pos-products"] }, (oldData) => {
        if (!oldData || !oldData.pages) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page, index) => {
            if (index === 0) {
              const existing = page.data || [];
              if (existing.some((p) => p.id === newProduct.id)) {
                return page;
              }
              return {
                ...page,
                data: [newProduct, ...existing],
              };
            }
            return page;
          }),
        };
      });

      // Optimistically insert into all-pos-products (standard query) cache
      queryClient.setQueriesData({ queryKey: ["all-pos-products"] }, (oldData) => {
        if (!oldData || !Array.isArray(oldData)) return oldData;
        if (oldData.some((p) => p.id === newProduct.id)) return oldData;
        return [newProduct, ...oldData];
      });

      onOpenChange(false);
      resetForm();

      if (onSuccess) {
        onSuccess(newProduct);
      }
    },
    onError: (error) => {
      const errorMsg =
        error?.response?.data?.message ||
        (error?.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(", ")
          : null) ||
        "Failed to create product";
      toast.error(errorMsg);
    },
  });

  const handleCreateProduct = () => {
    if (!newProductData.name || !newProductData.name.trim()) {
      toast.error("Product Name is required");
      return;
    }
    if (newProductData.cost === "" || newProductData.cost < 0) {
      toast.error("Valid Cost is required");
      return;
    }
    if (newProductData.sell_price === "" || newProductData.sell_price < 0) {
      toast.error("Valid Sell Price is required");
      return;
    }

    const categoryIds =
      Array.isArray(newProductData.categories) &&
        newProductData.categories.length > 0
        ? newProductData.categories
          .map((id) => Number(id))
          .filter((id) => !isNaN(id) && id > 0)
        : null;

    const costNum = parseFloat(newProductData.cost) || 0;
    const sellPriceNum = parseFloat(newProductData.sell_price) || 0;
    const sellPriceMinNum =
      newProductData.sell_price_min !== "" && newProductData.sell_price_min != null
        ? parseFloat(newProductData.sell_price_min) || sellPriceNum
        : sellPriceNum;
    const sellPriceMaxNum =
      newProductData.sell_price_max !== "" && newProductData.sell_price_max != null
        ? parseFloat(newProductData.sell_price_max) || sellPriceNum
        : sellPriceNum;

    const payload = {
      name: newProductData.name.trim(),
      sku: newProductData.sku ? newProductData.sku.trim() : null,
      short_description: newProductData.short_description || null,
      description: null,
      barcode: null,
      barcode_symbology: null,
      brand_id: newProductData.brand_id ? Number(newProductData.brand_id) : null,
      model_id: newProductData.model_id ? Number(newProductData.model_id) : null,
      unit_id: newProductData.unit_id ? Number(newProductData.unit_id) : null,
      categories: categoryIds && categoryIds.length > 0 ? categoryIds : null,
      cost: costNum,
      store_has_diff_cost: false,
      sell_price: sellPriceNum,
      store_has_diff_sell_price: false,
      sell_price_min: sellPriceMinNum,
      store_has_diff_sell_price_min: false,
      sell_price_max: sellPriceMaxNum,
      store_has_diff_sell_price_max: false,
      alert_quantity: Number(newProductData.alert_quantity) || 0,
      store_has_diff_alert_quantity: false,
      warranty_timeframe: newProductData.warranty_timeframe || "No Warranty",
      warranty: Number(newProductData.warranty) || 0,
      imei: null,
      condition: "new",
      upc_code: null,
      is_commission: false,
      display_on_pos: Boolean(newProductData.display_on_pos),
      is_taxable: true,
      tax_method: newProductData.tax_method || "exclusive",
      image: null,
      tracking_type: newProductData.tracking_type || "none",
      is_serialized: newProductData.tracking_type === "serial",
      manage_inventory: Boolean(newProductData.manage_inventory),
      has_variant: false,
      is_variant: false,
      status: newProductData.status || "active",
      is_expirable: false,
      expiry_discount_tiers: null,
      addons: null,
      type: newProductData.type || "simple",
    };

    createProductMutation.mutate(payload);
  };

  const resetForm = () => {
    setNewProductData({
      name: "",
      sku: "",
      short_description: "",
      unit_id: null,
      brand_id: null,
      model_id: null,
      categories: [],
      cost: 0,
      sell_price: 0,
      sell_price_min: 0,
      sell_price_max: 0,
      alert_quantity: 0,
      warranty: 0,
      type: "simple",
      tax_method: "exclusive",
      manage_inventory: defaultManageInventory,
      tracking_type: defaultTrackingType,
      display_on_pos: true,
      status: "active",
      warranty_timeframe: "No Warranty",
    });
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTrackingType, defaultManageInventory]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onOpenChange(false);
          resetForm();
        } else {
          onOpenChange(true);
        }
      }}
    >
      <DialogContent className="max-w-3xl p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Add New Product</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[80vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 py-4 mr-2">
            <div className="col-span-full space-y-2">
              <Label>
                Product Name <span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter product name"
                value={newProductData.name}
                onChange={(e) =>
                  setNewProductData({
                    ...newProductData,
                    name: e.target.value,
                  })
                }
                maxLength={255}
              />
            </div>

            <div className="col-span-full space-y-2">
              <Label>Short Description</Label>
              <DefaultEditor
                value={newProductData.short_description}
                onChange={(e) => {
                  if (e.target.value.length <= 255) {
                    setNewProductData({
                      ...newProductData,
                      short_description: e.target.value,
                    });
                  }
                }}
                containerProps={{
                  style: {
                    resize: "vertical",
                    height: "150px",
                    overflowY: "auto",
                  },
                }}
              />
              <p className="text-[10px] text-muted-foreground text-right mt-1">
                {(newProductData.short_description || "").length} / 255
              </p>
            </div>

            <div className="space-y-2">
              <Label>SKU</Label>
              <Input
                type="text"
                placeholder="Enter SKU"
                value={newProductData.sku}
                onChange={(e) =>
                  setNewProductData({
                    ...newProductData,
                    sku: e.target.value,
                  })
                }
                maxLength={255}
              />
            </div>

            <div className="space-y-2">
              <Label>Categories</Label>
              <ReactSelect
                isMulti
                options={categoryOptions}
                placeholder="Select Categories"
                value={categoryOptions.filter((opt) =>
                  newProductData.categories.includes(opt.value)
                )}
                onChange={(options) =>
                  setNewProductData({
                    ...newProductData,
                    categories: options
                      ? options
                        .map((o) => Number(o.value))
                        .filter((val) => !isNaN(val) && val > 0)
                      : [],
                  })
                }
                onInputChange={(val) => setCategorySearchInput(val)}
                maxMenuHeight={200}
              />
            </div>

            <div className="space-y-2">
              <Label>Brand / Make</Label>
              <ReactSelect
                options={brandOptions}
                placeholder="Select Brand"
                value={brandOptions.find(
                  (opt) => opt.value === newProductData.brand_id
                )}
                onChange={(option) => {
                  setNewProductData({
                    ...newProductData,
                    brand_id: option?.value ? Number(option.value) : null,
                    model_id: null,
                  });
                }}
                isClearable
                onInputChange={(val) => setBrandSearchInput(val)}
                maxMenuHeight={200}
              />
            </div>

            <div className="space-y-2">
              <Label>Model</Label>
              <ReactSelect
                options={modelOptions}
                placeholder={newProductData.brand_id ? "Select Model" : "Pick a brand first"}
                value={modelOptions.find(
                  (opt) => opt.value === newProductData.model_id
                )}
                onChange={(option) =>
                  setNewProductData({
                    ...newProductData,
                    model_id: option?.value ? Number(option.value) : null,
                  })
                }
                isClearable
                isDisabled={!newProductData.brand_id}
                onInputChange={(val) => setModelSearchInput(val)}
                maxMenuHeight={200}
              />
            </div>

            <div className="space-y-2">
              <Label>Unit</Label>
              <ReactSelect
                options={unitOptions}
                placeholder="Select Unit"
                value={unitOptions.find(
                  (opt) => opt.value === newProductData.unit_id,
                )}
                onChange={(option) =>
                  setNewProductData({
                    ...newProductData,
                    unit_id: option?.value ? Number(option.value) : null,
                  })
                }
                isClearable
                onInputChange={(val) => setUnitSearchInput(val)}
                maxMenuHeight={200}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Cost Price <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                placeholder="0.00"
                value={newProductData.cost}
                onChange={(e) =>
                  setNewProductData({
                    ...newProductData,
                    cost: parseFloat(e.target.value) || 0,
                  })
                }
                min="0"
                step="0.01"
              />
            </div>

            <div className="space-y-2">
              <Label>
                Selling Price <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                placeholder="0.00"
                value={newProductData.sell_price}
                onChange={(e) =>
                  setNewProductData({
                    ...newProductData,
                    sell_price: parseFloat(e.target.value) || 0,
                  })
                }
                min="0"
                step="0.01"
              />
            </div>

            <div className="space-y-2">
              <Label>Tax Method</Label>
              <ReactSelect
                options={[
                  { value: "exclusive", label: "Exclusive" },
                  { value: "inclusive", label: "Inclusive" },
                ]}
                placeholder="Select Tax Method"
                value={[
                  { value: "exclusive", label: "Exclusive" },
                  { value: "inclusive", label: "Inclusive" },
                ].find((opt) => opt.value === newProductData.tax_method)}
                onChange={(option) =>
                  setNewProductData({
                    ...newProductData,
                    tax_method: option?.value || "exclusive",
                  })
                }
                maxMenuHeight={200}
              />
            </div>

            <div className="space-y-2">
              <Label>Tracking Type</Label>
              <ReactSelect
                options={[
                  { value: "none", label: "None" },
                  { value: "standard", label: "Standard / Batch" },
                  { value: "serial", label: "Serial Number" },
                ]}
                placeholder="Select Tracking Type"
                value={[
                  { value: "none", label: "None" },
                  { value: "standard", label: "Standard / Batch" },
                  { value: "serial", label: "Serial Number" },
                ].find((opt) => opt.value === newProductData.tracking_type)}
                onChange={(option) => {
                  const val = option?.value || "none";
                  setNewProductData({
                    ...newProductData,
                    tracking_type: val,
                    manage_inventory: val !== "none",
                  });
                }}
                maxMenuHeight={200}
              />
            </div>

            <div className="col-span-full flex items-center justify-between mt-2 p-3 border rounded-lg">
              <div className="space-y-0.5">
                <Label>Manage Inventory</Label>
                <p className="text-[13px] text-muted-foreground">
                  Track stock levels for this product
                </p>
              </div>
              <Switch
                checked={newProductData.manage_inventory}
                onCheckedChange={(checked) => {
                  if (!checked && newProductData.tracking_type !== "none") {
                    toast.error(
                      `Manage inventory must be enabled when tracking type is ${newProductData.tracking_type}`,
                    );
                    return;
                  }
                  setNewProductData({
                    ...newProductData,
                    manage_inventory: checked,
                  });
                }}
              />
            </div>
          </div>
        </ScrollArea>
        <DialogFooter className="px-6 pb-6">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateProduct}
            disabled={createProductMutation.isPending}
          >
            {createProductMutation.isPending ? (
              <>
                <Spinner size={16} className="mr-2 text-current" />
                Creating...
              </>
            ) : (
              "Create Product"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
